import { useEffect, useState } from "react";
import { irropsApi } from "@/api";
import { Activity, AlertTriangle, CheckCircle, Clock, Plane, Users, Wrench, Zap, Plus } from "lucide-react";

const eventTypeConfig = {
  weather_delay: { label: "Weather Delay", color: "text-blue-400", bg: "bg-blue-400/10", border: "border-blue-400/20" },
  aog: { label: "AOG", color: "text-red-400", bg: "bg-red-400/10", border: "border-red-500/30" },
  crew_shortage: { label: "Crew Shortage", color: "text-orange-400", bg: "bg-orange-400/10", border: "border-orange-400/20" },
  atc_restriction: { label: "ATC Restriction", color: "text-purple-400", bg: "bg-purple-400/10", border: "border-purple-400/20" },
};

const severityConfig = {
  critical: "border-l-red-500 bg-red-500/5",
  high: "border-l-orange-500 bg-orange-500/5",
  medium: "border-l-amber-500 bg-amber-500/5",
};

function RecoveryOptions({ eventId }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    irropsApi.getRecovery(eventId).then(r => setOptions(r.data)).finally(() => setLoading(false));
  }, [eventId]);

  return (
    <div className="mt-4 border-t border-border/50 pt-4">
      <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">Recovery Options — Ranked by Safety</div>
      {loading ? <div className="text-xs text-muted-foreground">Loading options...</div> : (
        <div className="space-y-2">
          {options.map(opt => (
            <div key={opt.rank} onClick={() => setSelected(opt.rank)}
              className={`p-3 rounded-sm border cursor-pointer transition-all ${selected === opt.rank ? "border-primary bg-primary/5" : "border-border hover:border-primary/30"} ${opt.recommended ? "ring-1 ring-green-500/30" : ""}`}
              data-testid={`recovery-option-${opt.rank}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-muted-foreground">#{opt.rank}</span>
                  <span className="text-xs font-medium text-foreground">{opt.option}</span>
                  {opt.recommended && <span className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 px-1.5 py-0.5 rounded-sm font-bold">RECOMMENDED</span>}
                </div>
                <span className={`text-lg font-mono font-medium ${opt.safety_score >= 85 ? "text-green-400" : opt.safety_score >= 65 ? "text-amber-400" : "text-red-400"}`}>
                  {opt.safety_score}
                </span>
              </div>
              <div className="flex gap-4 mt-1">
                {[["Fatigue Impact", opt.fatigue_impact], ["Legality", opt.legality], ["Continuity", opt.operational_continuity]].map(([lbl, val]) => (
                  <div key={lbl} className="text-xs">
                    <span className="text-muted-foreground">{lbl}: </span>
                    <span className="text-foreground">{val}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {selected && (
            <button className="w-full mt-2 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors" data-testid="apply-recovery">
              Apply Option #{selected}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function NewEventModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ event_type: "weather_delay", title: "", description: "", affected_flights: 0, severity: "high" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await irropsApi.createEvent(form);
    setLoading(false);
    onCreated();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-sm w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="font-bold font-heading">Log IRROPS Event</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><Zap size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-4 space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-1">Event Type</label>
            <select value={form.event_type} onChange={e => setForm({...form, event_type: e.target.value})}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
              <option value="weather_delay">Weather Delay</option>
              <option value="aog">AOG</option>
              <option value="crew_shortage">Crew Shortage</option>
              <option value="atc_restriction">ATC Restriction</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-1">Title</label>
            <input value={form.title} onChange={e => setForm({...form, title: e.target.value})} required
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary" placeholder="Brief event title" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-1">Description</label>
            <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none h-20" placeholder="Describe the disruption..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-1">Affected Flights</label>
              <input type="number" value={form.affected_flights} onChange={e => setForm({...form, affected_flights: parseInt(e.target.value)})}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground font-bold block mb-1">Severity</label>
              <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})}
                className="w-full bg-background border border-border rounded-sm px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary">
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <button type="submit" disabled={loading} className="w-full py-2.5 bg-primary text-primary-foreground rounded-sm text-sm font-semibold disabled:opacity-60">
            {loading ? "Logging..." : "Log IRROPS Event"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function IrropsConsole() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);

  const fetchEvents = () => {
    irropsApi.getEvents().then(r => setEvents(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchEvents(); }, []);

  const active = events.filter(e => e.status === "active");
  const resolved = events.filter(e => e.status === "resolved" || e.status === "in_progress");

  return (
    <div className="p-6 space-y-5 fade-in-up" data-testid="irrops-console">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-1">Module 8</div>
          <h1 className="text-2xl font-black font-heading text-foreground">IRROPS Response Engine</h1>
        </div>
        <button onClick={() => setShowNewModal(true)} className="flex items-center gap-2 text-sm px-4 py-2 bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 transition-colors" data-testid="log-irrops-button">
          <Plus size={14} /> Log Event
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[["Active Events", active.length, "text-red-400"], ["In Progress", resolved.filter(e => e.status === "in_progress").length, "text-amber-400"], ["Resolved Today", resolved.filter(e => e.status === "resolved").length, "text-green-400"]].map(([lbl, val, col]) => (
          <div key={lbl} className="bg-card border border-border rounded-sm p-3">
            <div className={`text-2xl font-mono font-medium ${col}`}>{val}</div>
            <div className="text-xs text-muted-foreground mt-1 tracking-wider uppercase">{lbl}</div>
          </div>
        ))}
      </div>

      {active.length > 0 && (
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3 flex items-center gap-2">
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 animate-pulse" /> Active Disruptions
          </div>
          <div className="space-y-3">
            {active.map(event => {
              const tc = eventTypeConfig[event.event_type] || { label: event.event_type, color: "text-muted-foreground", bg: "bg-muted/20", border: "border-border" };
              return (
                <div key={event.id} className={`bg-card border border-l-4 border-border rounded-sm overflow-hidden ${severityConfig[event.severity] || "border-l-border"}`} data-testid={`irrops-event-${event.id}`}>
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold ${tc.color} ${tc.bg} border ${tc.border}`}>{tc.label}</span>
                          <span className="text-xs uppercase font-bold text-red-400">ACTIVE</span>
                        </div>
                        <h3 className="font-semibold text-foreground font-heading">{event.title}</h3>
                        <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{event.description}</p>
                      </div>
                    </div>
                    <div className="flex gap-4 mt-3">
                      {[["Flights Affected", event.affected_flights], ["Crew Affected", event.affected_crew], ["Severity", event.severity?.toUpperCase()]].map(([lbl, val]) => (
                        <div key={lbl} className="text-xs">
                          <div className="text-muted-foreground">{lbl}</div>
                          <div className="font-mono font-medium text-foreground">{val}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setExpandedId(expandedId === event.id ? null : event.id)}
                      className="mt-3 text-xs text-primary hover:underline flex items-center gap-1" data-testid={`recovery-toggle-${event.id}`}>
                      <Activity size={12} />
                      {expandedId === event.id ? "Hide" : "View"} Recovery Options
                    </button>
                  </div>
                  {expandedId === event.id && (
                    <div className="px-4 pb-4">
                      <RecoveryOptions eventId={event.id} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {resolved.length > 0 && (
        <div>
          <div className="text-xs tracking-widest uppercase text-muted-foreground font-bold mb-3">In Progress / Resolved</div>
          <div className="space-y-2">
            {resolved.map(event => {
              const tc = eventTypeConfig[event.event_type] || { label: event.event_type, color: "text-muted-foreground", bg: "bg-muted/20", border: "border-border" };
              return (
                <div key={event.id} className="bg-card border border-border rounded-sm p-3 flex items-center gap-4" data-testid={`irrops-resolved-${event.id}`}>
                  <span className={`inline-flex px-2 py-0.5 rounded-sm text-xs font-bold ${tc.color} ${tc.bg} border ${tc.border}`}>{tc.label}</span>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-foreground">{event.title}</div>
                    <div className="text-xs text-muted-foreground">{event.affected_flights} flights · {event.affected_crew} crew</div>
                  </div>
                  <span className={`text-xs font-bold uppercase ${event.status === "resolved" ? "text-green-400" : "text-amber-400"}`}>{event.status?.replace(/_/g, " ")}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {showNewModal && <NewEventModal onClose={() => setShowNewModal(false)} onCreated={fetchEvents} />}
    </div>
  );
}
